/**
 * HubSpot tracking helpers shared across the apps.
 *
 * The HubSpot loader sets a persistent `hubspotutk` cookie on the apex domain
 * (e.g. `.nativeteams.com`) which binds the visitor to whatever email we last
 * called `_hsq.push(['identify', { email }])` with. On a shared device this
 * causes subsequent form submissions from a different user to be merged onto
 * the previous user's HubSpot contact (their email is added as a secondary
 * email instead of creating a new contact).
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();interface HubSpotIdentifyTraits {
  email: string;
  [key: string]: string | number | boolean | null;
}

type HubSpotQueueCommand =
  | ['identify', HubSpotIdentifyTraits]
  | ['trackPageView']
  | ['setPath', string];

export interface HubSpotVisitorIdentification {
  email: string;
  token: string;
}

interface HubSpotConversationsWidget {
  load: () => void;
  remove: () => void;
  open: () => void;
  close: () => void;
}

interface HubSpotConversationsApi {
  widget: HubSpotConversationsWidget;
  clear: (options?: { resetWidget?: boolean }) => void;
}

interface HubSpotConversationsSettings {
  loadImmediately?: boolean;
  identificationEmail?: string;
  identificationToken?: string;
}

interface CachedVisitorIdentification extends HubSpotVisitorIdentification {
  expiresAt: number;
}

declare global {
  interface Window {
    _hsq?: HubSpotQueueCommand[];
    HubSpotConversations?: HubSpotConversationsApi;
    hsConversationsSettings?: HubSpotConversationsSettings;
    hsConversationsOnReady?: Array<() => void>;
  }
}

const HUBSPOT_COOKIE_NAMES = ['hubspotutk', '__hstc', '__hssc', '__hssrc'] as const;
const LAST_IDENTIFIED_EMAIL_KEY = 'nt:hs:last-identified-email';
const VISITOR_IDENTIFICATION_CACHE_KEY = 'nt:hs:visitor-identification';
const VISITOR_IDENTIFICATION_TTL_MS = 11 * 60 * 60 * 1000;
const VISITOR_IDENTIFICATION_PATH = '/api/hubspot/visitor-identification';

function getDomainCandidates(hostname: string): string[] {
  if (!hostname || hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return [];
  }

  const parts = hostname.split('.');
  const candidates: string[] = [];

  for (let i = 0; i < parts.length - 1; i++) {
    const domain = parts.slice(i).join('.');
    candidates.push(domain, `.${domain}`);
  }

  return candidates;
}

function deleteCookieEverywhere(name: string): void {
  if (typeof document === 'undefined') return;

  const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const domains = getDomainCandidates(window.location.hostname);

  document.cookie = `${name}=; expires=${past}; path=/; SameSite=Lax`;

  for (const domain of domains) {
    document.cookie = `${name}=; expires=${past}; path=/; domain=${domain}; SameSite=Lax`;
  }
}

/**
 * Wipes HubSpot tracking cookies (`hubspotutk`, `__hstc`, `__hssc`, `__hssrc`)
 * on every parent domain so the browser starts as a fresh, anonymous HubSpot
 * visitor.
 *
 * Only call this when the identified user actually changes (handled inside
 * `identifyHubSpotUser`). Do NOT wire it into logout: deleting these cookies
 * during the logout/redirect flow triggered a "Something went wrong" error
 * screen (see the reverted attempt in commit bea36f863), and doing it on every
 * load churns `hubspotutk` and breaks contact-targeted pop-ups.
 *
 * Note: this intentionally does NOT push `revokeCookieConsent`, which would
 * disable tracking entirely. Consent is managed separately via the inline
 * consent script in each app layout.
 *
 * Safe to call from any client component / browser-side code. No-ops on the
 * server.
 */
export function resetHubSpotVisitor(): void {
  if (typeof window === 'undefined') return;

  for (const name of HUBSPOT_COOKIE_NAMES) {
    deleteCookieEverywhere(name);
  }

  clearLastIdentifiedEmail();
  clearCachedVisitorIdentification();
}

function readLastIdentifiedEmail(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return localStorage.getItem(LAST_IDENTIFIED_EMAIL_KEY);
  } catch {
    return null;
  }
}

function setLastIdentifiedEmail(email: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LAST_IDENTIFIED_EMAIL_KEY, email);
  } catch {
    // ignore
  }
}

function clearLastIdentifiedEmail(): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(LAST_IDENTIFIED_EMAIL_KEY);
  } catch {
    // ignore storage access errors (e.g. third-party cookie blocks)
  }
}

function clearCachedVisitorIdentification(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(VISITOR_IDENTIFICATION_CACHE_KEY);
  } catch {
    // ignore storage access errors
  }
}

/**
 * Identifies the current user with HubSpot.
 *
 * IMPORTANT: this runs on every authenticated page load (via
 * `HubSpotIdentityBinding`), so it must NOT reset the visitor unconditionally.
 * Wiping `hubspotutk` on every load mints a new visitor token each time, which
 * stops HubSpot resolving the browser to a known contact — contact-targeted
 * pop-ups / CTAs then silently never appear.
 *
 * Instead we only reset when the identified email actually changes (a different
 * user on the same browser), and skip entirely when it's the same user. This
 * keeps `hubspotutk` stable across a session so pop-up targeting works, while
 * still protecting against cross-user contact merging — without touching the
 * logout flow (resetting there caused a "Something went wrong" error screen).
 *
 * Returns `true` when the identify call was queued, `false` when skipped
 * (already identified with the same email, no email provided, or SSR).
 */
export function identifyHubSpotUser(email: string | null | undefined): boolean {
  if (typeof window === 'undefined' || !email) return false;

  const normalized = email.trim().toLowerCase();
  if (!normalized) return false;

  const previous = readLastIdentifiedEmail();

  if (previous && previous !== normalized) {
    // Different user on the same browser — clear the prior visitor first.
    resetHubSpotVisitor();
  } else if (previous === normalized) {
    // Same user already identified this session — nothing to do.
    return false;
  }

  window._hsq = window._hsq || [];
  window._hsq.push(['identify', { email: normalized }]);
  window._hsq.push(['trackPageView']);

  setLastIdentifiedEmail(normalized);
  return true;
}

/**
 * Chat conversation identity is a different HubSpot system than `_hsq.identify`.
 * Tracking uses `hubspotutk`; the chat widget uses `messagesUtk` unless we pass
 * `identificationEmail` + `identificationToken` before `widget.load()`.
 */
let hasWarnedMissingPortalId = false;

export function getHubSpotPortalId(): string {
  const portalId = process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID || '';

  if (!portalId && !hasWarnedMissingPortalId) {
    hasWarnedMissingPortalId = true;
    console.warn(
      'HubSpot Portal ID not configured. Please set NEXT_PUBLIC_HUBSPOT_PORTAL_ID environment variable.'
    );
  }

  return portalId;
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function readCachedVisitorIdentification(email: string): HubSpotVisitorIdentification | null {
  if (typeof sessionStorage === 'undefined') return null;

  try {
    const raw = sessionStorage.getItem(VISITOR_IDENTIFICATION_CACHE_KEY);
    if (!raw) return null;

    const cached = JSON.parse(raw) as CachedVisitorIdentification;
    if (cached.email !== email || !cached.token || cached.expiresAt <= Date.now()) {
      sessionStorage.removeItem(VISITOR_IDENTIFICATION_CACHE_KEY);
      return null;
    }

    return { email: cached.email, token: cached.token };
  } catch {
    return null;
  }
}

function writeCachedVisitorIdentification(identification: HubSpotVisitorIdentification): void {
  if (typeof sessionStorage === 'undefined') return;

  try {
    const cached: CachedVisitorIdentification = {
      ...identification,
      expiresAt: Date.now() + VISITOR_IDENTIFICATION_TTL_MS,
    };
    sessionStorage.setItem(VISITOR_IDENTIFICATION_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // ignore storage access errors
  }
}

export function applyHubSpotConversationIdentity(
  identification: HubSpotVisitorIdentification
): void {
  if (typeof window === 'undefined') return;

  window.hsConversationsSettings = {
    ...window.hsConversationsSettings,
    loadImmediately: false,
    identificationEmail: identification.email,
    identificationToken: identification.token,
  };
}

export async function fetchHubSpotVisitorIdentification(
  email: string
): Promise<HubSpotVisitorIdentification | null> {
  const normalized = normalizeEmail(email);
  if (!normalized) return null;

  const cached = readCachedVisitorIdentification(normalized);
  if (cached) return cached;

  try {
    const response = await fetch(VISITOR_IDENTIFICATION_PATH, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return null;

    const data = (await response.json()) as Partial<HubSpotVisitorIdentification>;
    if (typeof data.email !== 'string' || typeof data.token !== 'string') return null;

    const identification: HubSpotVisitorIdentification = {
      email: normalizeEmail(data.email),
      token: data.token,
    };

    if (identification.email !== normalized || !identification.token) return null;

    writeCachedVisitorIdentification(identification);
    return identification;
  } catch {
    return null;
  }
}
